import { useState, type ReactElement, type ComponentProps } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { NotaryProfileSettingKeys, NotaryProfileSettingValues } from "graphql_globals";
import Button from "common/core/button";
import SROnly from "common/core/screen_reader";
import { Paragraph } from "common/core/typography";
import UpdateNotaryProfileSettingMutation, {
  type UpdateNotaryProfileSetting_updateNotaryProfileSetting_notaryProfile as NotaryProfile,
} from "common/tech_check/video/update_notary_profile_setting_mutation.graphql";
import CloseIcon from "assets/images/av_settings/close.svg";
import { useRawMutation } from "util/graphql/mutation";

import Styles from "./index.module.scss";

type Props = {
  notaryProfile: NotaryProfile;
};

type ContainerProps = {
  onToggleBackgroundSettings: () => void;
  children: ReactElement<ComponentProps<typeof BackgroundSettings>>;
};

const LABELS = defineMessages({
  background: {
    id: "bd651419-ab98-4e32-a5d5-f2cd9b93ff1a",
    defaultMessage: "Video Background",
  },
  [NotaryProfileSettingValues.NO_BLUR]: {
    id: "613a8a9e-7139-44af-9af0-4c9cd0396d81",
    defaultMessage: "No Blur",
  },
  [NotaryProfileSettingValues.PARTIAL_BLUR]: {
    id: "8e85c3a3-5bd5-4e78-ba19-a4057b98fecd",
    defaultMessage: "Partial Blur",
  },
  [NotaryProfileSettingValues.FULL_BLUR]: {
    id: "f73f5185-201c-4a61-a9d2-bc3dfb5f1532",
    defaultMessage: "Full Blur",
  },
  backgroundSettingModalTitle: {
    id: "82764f45-a975-4d59-b39e-81aac067c617",
    defaultMessage: "Video background settings",
  },
});

export function BackgroundSettingsContainer({
  onToggleBackgroundSettings,
  children,
}: ContainerProps) {
  const intl = useIntl();
  return (
    <div
      role="dialog"
      aria-label={intl.formatMessage(LABELS.backgroundSettingModalTitle)}
      className={Styles.container}
    >
      <div className="AudioVideoContainer--Header">
        <h5>{intl.formatMessage(LABELS.backgroundSettingModalTitle)}</h5>
        <button
          type="button"
          className="AudioVideoContainer--Header--Close"
          onClick={onToggleBackgroundSettings}
        >
          <img src={CloseIcon} alt="" />
          <SROnly>
            <FormattedMessage
              id="bbc00d3e-b32a-401f-9cae-0e07b9d31e7a"
              defaultMessage="Close background settings modal"
            />
          </SROnly>
        </button>
      </div>
      {children}
    </div>
  );
}

function BackgroundSettings({ notaryProfile }: Props) {
  const intl = useIntl();
  const [backgroundSetting, setBackgroundSetting] = useState(
    notaryProfile.backgroundSetting || NotaryProfileSettingValues.NO_BLUR,
  );
  const [updateNotaryProfileSettingMutateFn, updateNotaryProfileSettingStatus] = useRawMutation(
    UpdateNotaryProfileSettingMutation,
  );

  const handleBackgroundSelect = async (settingValue: NotaryProfileSettingValues) => {
    await updateNotaryProfileSettingMutateFn({
      variables: {
        input: {
          notaryProfileId: notaryProfile.id,
          settingName: NotaryProfileSettingKeys.MEETING_BACKGROUND,
          settingValue,
        },
      },
    }).then(() => setBackgroundSetting(settingValue));
  };

  return (
    <div className={Styles.settings}>
      <Paragraph textAlign="center">
        <FormattedMessage
          id="cfabf2d4-9230-4b4c-b3c2-4b396c9742e1"
          defaultMessage="Background blur effect"
        />
      </Paragraph>
      <div>
        {[
          NotaryProfileSettingValues.NO_BLUR,
          NotaryProfileSettingValues.PARTIAL_BLUR,
          NotaryProfileSettingValues.FULL_BLUR,
        ].map((background) => {
          return (
            <Button
              variant={backgroundSetting === background ? "primary" : "secondary"}
              buttonColor="action"
              disabled={updateNotaryProfileSettingStatus.loading}
              key={background}
              onClick={() => handleBackgroundSelect(background)}
              withIcon={{ name: background.toLowerCase(), placement: "left" }}
            >
              {intl.formatMessage(LABELS[background])}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export default BackgroundSettings;
